"use client";

/**
 * ElectronicsVariantSelector — shop "Buy" dialog for ELECTRONICS products that
 * have variant axes (colour and/or storage/spec). Mirrors the POS electronics
 * flow and the product-details page: the customer picks a colour and/or
 * storage option, we build the canonical variantId with `buildVariantId`
 * (variant-{colorId}-{storage|default}) and show the per-variant price via
 * `resolveVariantPrice`. The server is the price authority on add — it resolves
 * the same variantId — so we only need to pass variantId + selectedVariants.
 *
 * Live per-variant stock is read from Convex `getProductStock` so the quantity
 * can never exceed what's actually available for the chosen variant.
 */

import { useState, useEffect } from "react";
import { Plus, Minus, X } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import { buildVariantId, resolveVariantPrice } from "@/lib/utils/variantPricing";
import { photosForColour } from "@/lib/config/colourPhotos";

interface EcColor {
  id?: string;
  name: string;
  hex?: string;
  isMulticolor?: boolean;
  multicolorList?: string[];
}

interface ElectronicsVariantSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  product: any;
  onConfirm: (
    quantity: number,
    variantId: string | undefined,
    selectedVariants: Record<string, any> | undefined,
  ) => Promise<void> | void;
}

const PLACEHOLDER = "/images/placeholder-product.jpg";

export function ElectronicsVariantSelector({
  isOpen,
  onClose,
  product,
  onConfirm,
}: ElectronicsVariantSelectorProps) {
  const colors: EcColor[] = product?.electronicsAttributes?.colors || [];
  const storageOptions: string[] =
    product?.electronicsAttributes?.storageOptions || [];
  const axisLabel: string =
    product?.electronicsAttributes?.variantAxisLabel || "Option";
  const currency: string = product?.currency || "UGX";

  const [selectedColorId, setSelectedColorId] = useState<string | undefined>();
  const [selectedColorName, setSelectedColorName] = useState<string | undefined>();
  const [selectedStorage, setSelectedStorage] = useState<string | undefined>();
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  // Reset + auto-select single options whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setQuantity(1);
    setAdding(false);
    if (colors.length === 1) {
      setSelectedColorId(colors[0].id);
      setSelectedColorName(colors[0].name);
    } else {
      setSelectedColorId(undefined);
      setSelectedColorName(undefined);
    }
    setSelectedStorage(storageOptions.length === 1 ? storageOptions[0] : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Live stock for the product (per-variant) — only while open.
  const liveStock: any = useQuery(
    api.products.shop.getProductStock,
    isOpen && product?._id
      ? { productId: product._id as Id<"products"> }
      : "skip",
  );
  const stockLoading = isOpen && liveStock === undefined;

  const hasColors = colors.length > 0;
  const hasStorage = storageOptions.length > 0;
  const ready =
    (!hasColors || !!selectedColorId) && (!hasStorage || !!selectedStorage);

  // Electronics variants are keyed `variant-{colorId}-{storage|default}`.
  const variantId = buildVariantId(
    selectedColorId,
    undefined,
    selectedStorage || (selectedColorId ? "default" : undefined),
  );

  // Available stock for the chosen variant (falls back to total while a
  // selection is incomplete). `null` = still loading / unknown.
  const availableStock: number | null = (() => {
    if (!liveStock) return null;
    const vs = liveStock.variantStock || {};
    if (variantId && vs[variantId] !== undefined) return vs[variantId];
    return typeof liveStock.totalStock === "number" ? liveStock.totalStock : null;
  })();

  // Clamp the quantity to whatever stock is available for the selection.
  useEffect(() => {
    if (availableStock !== null && availableStock > 0 && quantity > availableStock) {
      setQuantity(availableStock);
    }
  }, [availableStock, quantity]);

  if (!isOpen) return null;

  const price = resolveVariantPrice(product, variantId);
  // The chosen colour's own photo when it has one (lib/config/colourPhotos.ts).
  const heroImage =
    photosForColour(product?.electronicsAttributes, selectedColorId)?.[0] ||
    (product?.images && product.images[0]) || product?.imageUrl || PLACEHOLDER;
  const outOfStock = availableStock !== null && availableStock <= 0;
  const atMax = availableStock !== null && quantity >= availableStock;

  const buttonLabel = (() => {
    if (adding) return "Adding…";
    const missing: string[] = [];
    if (hasColors && !selectedColorId) missing.push("colour");
    if (hasStorage && !selectedStorage) missing.push(axisLabel.toLowerCase());
    if (missing.length === 1) return `Select ${missing[0]}`;
    if (missing.length === 2) return `Select ${missing[0]} & ${missing[1]}`;
    if (ready && outOfStock) return "Out of Stock";
    return `Add ${quantity} to Cart`;
  })();

  const canAdd = ready && !adding && !outOfStock && (availableStock === null || quantity <= availableStock);

  const handleAdd = async () => {
    if (!ready) {
      toast.error(buttonLabel);
      return;
    }
    if (outOfStock) {
      toast.error("This variant is out of stock");
      return;
    }
    if (availableStock !== null && quantity > availableStock) {
      toast.error(`Only ${availableStock} available`);
      setQuantity(availableStock);
      return;
    }
    setAdding(true);
    try {
      const colorObj = colors.find((c) => c.id === selectedColorId);
      // NOTE: the website addToCart validator only allows
      // color/colorHex/isMulticolor/multicolorList/size — NOT colorId. The
      // colour id is already encoded in variantId, so we must not send it here.
      const selectedVariants: Record<string, any> = {};
      if (selectedColorName) {
        selectedVariants.color = selectedColorName;
        if (colorObj?.hex) selectedVariants.colorHex = colorObj.hex;
        if (colorObj?.isMulticolor) {
          selectedVariants.isMulticolor = true;
          selectedVariants.multicolorList = colorObj.multicolorList;
        }
      }
      if (selectedStorage) selectedVariants.size = selectedStorage;

      await onConfirm(
        quantity,
        variantId,
        Object.keys(selectedVariants).length ? selectedVariants : undefined,
      );
      onClose();
    } catch (err: any) {
      toast.error(err?.message || "Failed to add to cart");
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        style={{ zIndex: 9998 }}
        onClick={onClose}
      />
      <div
        className="fixed inset-0 flex items-end justify-center pointer-events-none sm:items-center"
        style={{ zIndex: 9999 }}
      >
        <div
          className="pointer-events-auto relative flex w-full flex-col rounded-t-2xl bg-white shadow-2xl sm:max-w-md sm:rounded-2xl"
          style={{ maxHeight: "min(95vh, 95dvh)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden rounded-t-2xl">
            {/* Hero image */}
            <div className="relative w-full overflow-hidden rounded-t-2xl">
              <div className="relative w-full" style={{ aspectRatio: "1/1", maxHeight: "min(40vh, 300px)" }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={heroImage}
                  alt={product?.name || "Product"}
                  className="absolute inset-0 h-full w-full object-cover object-center"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = PLACEHOLDER;
                  }}
                />
              </div>
              <button
                onClick={onClose}
                className="absolute right-3 top-3 z-10 rounded-full p-2 text-white shadow-lg transition-all hover:scale-110"
                style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3 pt-10 sm:p-4">
                <h3 className="line-clamp-1 text-base font-bold leading-tight text-white drop-shadow-md sm:text-lg">
                  {product?.name}
                </h3>
                <div className="mt-0.5 text-sm font-medium text-white/90">
                  {currency} {Math.floor(price).toLocaleString()}
                </div>
              </div>
            </div>

            <div className="space-y-5 p-4 sm:p-5">
              {/* Colour */}
              {hasColors && (
                <div>
                  <label className="mb-2 block text-xs font-semibold text-gray-800">Colour</label>
                  <div className="grid grid-cols-5 gap-2.5">
                    {colors.map((color, idx) => (
                      <div key={color.id || idx} className="flex flex-col items-center gap-1">
                        <button
                          onClick={() => {
                            setSelectedColorId(color.id);
                            setSelectedColorName(color.name);
                          }}
                          title={color.name}
                          className={cn(
                            "h-9 w-9 rounded-full border-2 shadow-md transition-all hover:scale-110 sm:h-10 sm:w-10",
                            selectedColorId === color.id
                              ? "border-black ring-2 ring-black ring-offset-2"
                              : "border-gray-200",
                          )}
                          style={
                            color.isMulticolor
                              ? { background: `conic-gradient(${(color.multicolorList || [color.hex || "#888", "#fff"]).join(", ")})` }
                              : { backgroundColor: color.hex || "#888" }
                          }
                        />
                        <span className={cn(
                          "line-clamp-1 w-full text-center text-[9px] capitalize sm:text-[10px]",
                          selectedColorId === color.id ? "font-semibold text-black" : "text-gray-500",
                        )}>
                          {color.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Storage / spec axis */}
              {hasStorage && (
                <div>
                  <label className="mb-2 block text-xs font-semibold text-gray-800">{axisLabel}</label>
                  <div className="grid grid-cols-3 gap-2">
                    {storageOptions.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => setSelectedStorage(opt)}
                        className={cn(
                          "rounded-lg border-2 px-2 py-2 text-xs font-medium transition-all hover:scale-105",
                          selectedStorage === opt
                            ? "border-black bg-black text-white"
                            : "border-gray-200 bg-transparent text-gray-800 hover:border-gray-400",
                        )}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Stock + quantity */}
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 sm:p-4">
                <div className="mb-3 flex items-center justify-between border-b border-gray-200 pb-2">
                  <span className="text-sm font-medium text-gray-500">Available stock</span>
                  {stockLoading ? (
                    <span className="text-sm text-gray-400">Loading…</span>
                  ) : availableStock === null ? (
                    <span className="text-sm text-gray-400">—</span>
                  ) : (
                    <span
                      className={cn(
                        "text-sm font-bold",
                        availableStock <= 0
                          ? "text-red-500"
                          : availableStock <= 5
                            ? "text-orange-500"
                            : "text-green-600",
                      )}
                    >
                      {availableStock <= 0
                        ? "Out of stock"
                        : availableStock <= 5
                          ? `Only ${availableStock} left`
                          : `${availableStock} units`}
                    </span>
                  )}
                </div>

                <label className="mb-2 block text-xs font-semibold text-gray-800">Quantity</label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={quantity <= 1}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-gray-200 text-gray-700 disabled:opacity-40"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="min-w-[2.5rem] text-center text-xl font-bold text-gray-900">{quantity}</span>
                  <button
                    onClick={() =>
                      setQuantity((q) =>
                        availableStock !== null ? Math.min(availableStock, q + 1) : q + 1,
                      )
                    }
                    disabled={atMax || outOfStock}
                    title={atMax ? "Maximum available stock reached" : undefined}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-gray-200 text-gray-700 disabled:opacity-40"
                    aria-label="Increase quantity"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky CTA */}
          <div className="shrink-0 rounded-b-2xl border-t border-gray-100 bg-white p-3 pt-2 sm:p-4">
            <button
              onClick={handleAdd}
              disabled={!canAdd}
              className={cn(
                "h-12 w-full rounded-xl text-base font-semibold transition-all",
                canAdd
                  ? "bg-black text-white hover:bg-black/90"
                  : "cursor-not-allowed bg-gray-100 text-gray-400 opacity-60",
              )}
            >
              {buttonLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
